import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { resetCardDetails, getCardDetailRequest } from 'actions/cardActions';
import { selectCardDetailModalContext } from 'selectors/cardDetailModalSelectors';

import './card-detail-modal.scss';
import CardDetail from '../../components/card-detail/card-detail';
import CardHistory from '../../components/card-history/card-history';
import TransactionList from '../../components/transaction-list/transaction-list';
import TransferCardsForm from '../../components/transfer-cards-form/transfer-cards-form';
import {
  cardStatuses,
  editableCardStatuses,
  allowedCardActionsRequiredVerificationMethods,
} from 'utilities/card-utilities';
import Label from 'constants/labels';
import Env from 'Env';
import { Tabs } from '@ampol/design-system';
import TabWrapper from 'components/tab-wrapper/TabWrapper';
import CardActions from './card-actions';
import AmpchargeBanner from '../../../../assets/ampcharge-banner.svg';
import InfoBanner from 'components/_global/InfoBanner/InfoBanner';

/**
 * Tracks whether the viewport is at least medium width (768px).
 * Extracted from CardDetailModal to comply with the Rules of Hooks —
 * hooks must not be called inside nested functions.
 */
function useBreakpointMd(): boolean {
  const [isMd, setIsMd] = useState(() => window.innerWidth > 768);

  useEffect(() => {
    const handleResize = () => setIsMd(window.innerWidth > 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isMd;
}

type CardDetailModalProps = {
  selectedCard: {
    cardNumber: string;
    parent?: string;
  };
  onEditCardClick: () => void;
};

type CardDetailsTab = {
  id: string;
  label: string;
};

const CardDetailModal: React.FC<CardDetailModalProps> = ({ selectedCard, onEditCardClick }) => {
  const dispatch = useDispatch();

  const {
    canReadWrite,
    assignedGrandfatherClientNumber,
    cardDetail,
    isPreviousOrReplacementCardClicked,
    previousOrReplacementCardNumber,
    isResetSelectedContent,
    cardDetailLoading,
    cardDetailError,
    updateSingleCardStatusSuccess,
    activateCardLoaded,
    singleCardTransferLoaded,
  } = useSelector(selectCardDetailModalContext);

  // onEditCardClick comes from the parent and may be recreated on every parent render.
  // Storing it in a ref prevents it from invalidating the tabs useMemo on each parent render.
  // Assigned synchronously during render — the ref is only ever read inside event handlers
  // (which run after render), so this is safe and avoids a no-dep-array Effect.
  const onEditCardClickRef = useRef(onEditCardClick);
  onEditCardClickRef.current = onEditCardClick;

  const cardNumber = isPreviousOrReplacementCardClicked ? cardDetail.cardNumber : selectedCard.cardNumber;
  const cardParent = selectedCard.parent ?? '';
  const isEvWay4Enabled = typeof Env.getIsEvWay4Enabled === 'function' ? Env.getIsEvWay4Enabled() : false;

  const [currentTabId, setCurrentTabId] = useState('card-detail');

  const isMenuItemVisible =
    editableCardStatuses.includes(cardDetail?.currentStatus) && canReadWrite && !cardDetailLoading && !cardDetailError;

  const isPinActionAllowed = allowedCardActionsRequiredVerificationMethods.includes(cardDetail.verifyPosPrompt);

  useEffect(() => {
    if (
      (updateSingleCardStatusSuccess || activateCardLoaded || singleCardTransferLoaded) &&
      assignedGrandfatherClientNumber
    ) {
      dispatch(getCardDetailRequest(selectedCard.cardNumber, assignedGrandfatherClientNumber));
    }
  }, [
    updateSingleCardStatusSuccess,
    activateCardLoaded,
    singleCardTransferLoaded,
    selectedCard?.cardNumber,
    assignedGrandfatherClientNumber,
    dispatch,
  ]);

  useEffect(() => {
    if (isPreviousOrReplacementCardClicked && previousOrReplacementCardNumber) resetTab();
    if (isResetSelectedContent) {
      setCurrentTabId('cardhistory');
    }
  }, [previousOrReplacementCardNumber, isResetSelectedContent, cardDetail]);

  useEffect(() => {
    return () => {
      dispatch(resetCardDetails());
    };
  }, [dispatch]);

  function resetTab() {
    setCurrentTabId('card-detail');
  }

  const ampchargeInfoBanner = () => {
    return (
      <>
        {cardDetail?.ampChargeEnabled && (
          <div className='mb-3'>
            <InfoBanner message={Label.EV_CHARGING_ENABLED_NOTIFICATION} type='ampcharge' />
          </div>
        )}
      </>
    );
  };

  const evInfoBanner = (label: string) => {
    return (
      <>
        {cardDetail?.ampChargeEnabled && (
          <div className='mb-3'>
            <InfoBanner message={label} type='default' />
          </div>
        )}
      </>
    );
  };

  const tabs = useMemo(
    () => [
      {
        id: 'card-detail',
        label: 'Card Detail',
        hasAccess: true,
        children: (
          <TabWrapper>
            {ampchargeInfoBanner()}
            <CardDetail
              onClickEdit={() => onEditCardClickRef.current()}
              cardNumber={cardNumber}
              cardParent={cardParent}
            />
          </TabWrapper>
        ),
      },
      {
        id: 'card-actions',
        label: 'Card Actions',
        hasAccess: !cardDetailLoading,
        children: (
          <TabWrapper>
            {ampchargeInfoBanner()}
            {evInfoBanner(Label.EV_CHARGING_PIN_MANAGEMENT_NOTIFICATION)}
            <CardActions
              cardNumber={cardNumber}
              cardParent={cardParent}
              closeForm={resetTab}
              showUpdatePinForm={isMenuItemVisible && isPinActionAllowed}
              showResetPinForm={isMenuItemVisible && isPinActionAllowed}
              showUpdateStatusForm={
                canReadWrite &&
                !cardDetailLoading &&
                !cardDetailError &&
                cardStatuses.find((status) => status.label === cardDetail?.currentStatus)?.allowedUpdateTargets
                  ?.length !== 0
              }
            />
          </TabWrapper>
        ),
      },
      {
        id: 'transfercards',
        label: Label.TRANSFER_CARD,
        // Feature flag: When VITE_EV_WAY4 is disabled, block transfer for AmpCharge cards
        hasAccess: isEvWay4Enabled ? isMenuItemVisible : isMenuItemVisible && !cardDetail?.ampChargeEnabled,
        children: (
          <TabWrapper>
            {ampchargeInfoBanner()}
            <TransferCardsForm inModal closeForm={resetTab} />
          </TabWrapper>
        ),
      },
      {
        id: 'cardtransactions',
        label: 'Transactions',
        hasAccess: true,
        children: (
          <TabWrapper>
            {ampchargeInfoBanner()}
            {/* Feature flag: hide transactions EV notification when Way4 is ON (EV sessions are included). */}
            {!isEvWay4Enabled && evInfoBanner(Label.EV_CHARGING_TRANSACTIONS_NOTIFICATION)}
            <TransactionList accountId={cardParent} cardNo={cardNumber} modalType={'card'} />
          </TabWrapper>
        ),
      },
      {
        id: 'cardhistory',
        label: 'History and Status',
        hasAccess: true,
        children: (
          <TabWrapper>
            {ampchargeInfoBanner()}
            <CardHistory />
          </TabWrapper>
        ),
      },
    ],
    // onEditCardClick is intentionally excluded — it is stored in onEditCardClickRef and accessed
    // via the ref wrapper, so the tabs array remains stable even when the parent re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cardDetail, cardDetailLoading, cardDetailError, canReadWrite]
  );

  const currentTab = tabs.find((tab) => tab.id === currentTabId) || tabs[0];

  useEffect(() => {
    // If currentTab doesn't exist in new tabs, reset to default
    if (!tabs.some((tab) => tab.id === currentTabId)) {
      resetTab();
    }
  }, [tabs, currentTabId]);

  const handleTabClick = (tabItem: CardDetailsTab) => {
    window.dataLayer.push({
      event: 'Card_Portal_Individual_Card_Detail_Nav',
      nav_item: tabItem?.label,
    });
    setCurrentTabId(tabItem.id);
  };

  const isWidthMd = useBreakpointMd();
  const bannerImage =
    cardDetail?.ampChargeEnabled && isWidthMd
      ? {
          url: AmpchargeBanner,
          description: 'Ampcharge banner',
        }
      : null;

  return (
    <div className='w-full -mt-[35px]'>
      <Tabs
        items={tabs}
        currentTab={currentTab}
        banner={bannerImage}
        onTabSelected={handleTabClick}
        className='w-full'
        isSticky
      />
    </div>
  );
};

export default CardDetailModal;
